import { groupBy } from "helpers";
import { chemistryMeasurementKey } from "components/materialChemistry/chemistryMeasurements";
import { useCallback } from "react";
import { useUnitsFormatter, useTenantTranslation } from "hooks/formatters";
import { useMassFormatter } from "hooks/useMassFormatter";
import { ParameterConflict } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";

const cellKey = (chefGroupId: number, coefficientsSignature: string): string =>
  `${chefGroupId}::${coefficientsSignature}`;

export interface IndexedConflicts {
  /** Every conflict for a chef group, whether cell-specific or grade-wide. */
  byChefGroup: Map<number, ParameterConflict[]>;
  /** Conflicts pinned to a material-limit cell, keyed by chef group and the
   * limit's (name-based) coefficients signature. */
  byCell: Map<string, ParameterConflict[]>;
}

export const indexConflicts = (
  conflicts: ParameterConflict[]
): IndexedConflicts => {
  const byChefGroup = new Map<number, ParameterConflict[]>();
  const byCell = new Map<string, ParameterConflict[]>();
  for (const conflict of conflicts) {
    byChefGroup.set(conflict.chef_group_id, [
      ...(byChefGroup.get(conflict.chef_group_id) ?? []),
      conflict,
    ]);
    if (conflict.coefficients_signature !== null) {
      const key = cellKey(
        conflict.chef_group_id,
        conflict.coefficients_signature
      );
      byCell.set(key, [...(byCell.get(key) ?? []), conflict]);
    }
  }
  return { byChefGroup, byCell };
};

export const conflictsForCell = (
  index: IndexedConflicts,
  chefGroupId: number,
  coefficientsSignature: string
): ParameterConflict[] =>
  index.byCell.get(cellKey(chefGroupId, coefficientsSignature)) ?? [];

/** A stable list key for a conflict, which has no identifier of its own. */
export const conflictKey = (conflict: ParameterConflict): string =>
  [
    conflict.chef_group_id,
    conflict.kind,
    conflict.coefficients_signature ?? "",
    conflict.basket_number ?? "",
    conflict.chemical_constraint_name ?? "",
    conflict.material_name ?? "",
    ...conflict.steel_grade_names,
  ].join("|");

export const parameterConflictSummaryKey = (
  count: number
): "parameterConflictSummarySingular" | "parameterConflictSummaryPlural" =>
  count === 1
    ? "parameterConflictSummarySingular"
    : "parameterConflictSummaryPlural";

export const formatConflictMessage = ({
  conflict,
  limitLabel,
  t,
  formatMass,
  formatVolume,
  formatNumber,
}: {
  conflict: ParameterConflict;
  limitLabel: string;
  t: ReturnType<typeof useTenantTranslation>["t"];
  formatMass: (mass: number) => string;
  formatVolume: (volume: number) => string;
  formatNumber: ReturnType<typeof useFormatNumber>;
}): string => {
  const percent = (value: number | null): string =>
    value === null ? "" : `${formatNumber(3)(value)}%`;
  const failureRate = percent(conflict.max_failure_rate);
  const grades =
    conflict.steel_grade_names.length === 1
      ? conflict.steel_grade_names[0]
      : t("nSteelGrades", { count: conflict.steel_grade_names.length });
  const required =
    conflict.required_mass === null ? "" : formatMass(conflict.required_mass);
  const available =
    conflict.available_mass === null ? "" : formatMass(conflict.available_mass);
  const chemistryParameters = {
    grades,
    constraint: conflict.chemical_constraint_name ?? "",
    achievable: percent(conflict.achievable_weight_percent),
    tolerance: percent(conflict.tolerance_weight_percent),
    failureRate,
  };
  switch (conflict.kind) {
    case "limit_forces_volume_breach":
    case "fixed_mix_exceeds_volume":
      return t(
        conflict.kind === "limit_forces_volume_breach"
          ? "conflictLimitVolume"
          : conflict.basket_number !== null
            ? "conflictFixedMixBasketVolume"
            : "conflictFixedMixVolume",
        {
          limit: limitLabel,
          basket: conflict.basket_number,
          grades,
          required:
            conflict.required_volume == null
              ? ""
              : formatVolume(conflict.required_volume),
          available:
            conflict.available_volume == null
              ? ""
              : formatVolume(conflict.available_volume),
        }
      );
    case "minimum_usable_mass_exceeds_capacity":
      return t("conflictMinimumUsableMass", {
        limit: limitLabel,
        required,
        available,
      });
    case "fixed_mix_exceeds_tapped_capacity":
      return t("conflictFixedMixTappedMass", { grades, required, available });
    case "fixed_mix_below_tapped_minimum":
      return t("conflictFixedMixTappedMinimum", {
        grades,
        required,
        available,
      });
    case "fixed_mix_exceeds_charged_capacity":
      return t(
        conflict.basket_number !== null
          ? "conflictFixedMixBasketMass"
          : "conflictFixedMixChargedMaximum",
        {
          basket: conflict.basket_number,
          grades,
          required,
          available,
        }
      );
    case "fixed_mix_below_charged_minimum":
      return t("conflictFixedMixChargedMinimum", {
        grades,
        required,
        available,
      });
    case "material_limit_excludes_material":
      return t("conflictMaterialExcluded", {
        material: conflict.material_name ?? "",
        limit: limitLabel,
        required,
        available,
      });
    case "fixed_mix_below_minimum_usable_mass":
      return t("conflictFixedMixUsableMass", {
        grades,
        required,
        available,
        material: conflict.material_name ?? "",
      });
    case "per_basket_minimum_exceeds_capacity":
      return t("conflictPerBasketMinOverCapacity", {
        basket: conflict.basket_number ?? "",
        limit: limitLabel,
        required,
        available,
      });
    case "heat_minimum_exceeds_capacity":
      return t("conflictHeatMinOverCapacity", {
        limit: limitLabel,
        required,
        available,
      });
    case "heat_minimum_exceeds_tapped_capacity":
      return t("conflictHeatMinOverTappedCapacity", {
        limit: limitLabel,
        required,
        available,
      });
    case "total_maximum_below_basket_minimums":
      return t("conflictTotalMaxBelowBasketMins", {
        limit: limitLabel,
        required,
        available,
      });
    case "total_minimum_above_basket_maximums":
      return t("conflictTotalMinAboveBasketMaxes", {
        limit: limitLabel,
        required,
        available,
      });
    case "coalesced_limits_inconsistent":
      return t(
        conflict.basket_number === null
          ? "conflictCoalescedLimitsInconsistent"
          : "conflictBasketLimitsInconsistent",
        {
          basket: conflict.basket_number ?? "",
          limit: limitLabel,
          required,
          available,
        }
      );
    case "chemistry_ceiling_unreachable":
      return t("conflictChemistryCeilingUnreachable", {
        ...chemistryParameters,
        material: conflict.material_name ?? "",
      });
    case "fixed_mix_chemistry_breach":
      return t("conflictFixedMixChemistryBreach", chemistryParameters);
    case "limit_forces_chemistry_breach":
      return t("conflictLimitForcesChemistryBreach", {
        ...chemistryParameters,
        limit: limitLabel,
      });
  }
};

export const useConflictMessage = (): ((
  conflict: ParameterConflict,
  limitLabel: string
) => string) => {
  const { t } = useTenantTranslation();
  const { formatMass } = useMassFormatter();
  const formatNumber = useFormatNumber();
  const units = useUnitsFormatter(false);

  return useCallback(
    (conflict, limitLabel) =>
      formatConflictMessage({
        conflict,
        limitLabel,
        t,
        formatMass,
        formatVolume: (volume) =>
          `${formatNumber(2)(volume)}${units("volume")}`,
        formatNumber,
      }),
    [t, formatMass, formatNumber, units]
  );
};

export const indexChemistryConflicts = (
  conflicts: ParameterConflict[]
): Map<string, ParameterConflict[]> =>
  new Map(
    Object.entries(
      groupBy(
        conflicts.flatMap((conflict) =>
          conflict.chemistry_measurements.map((measurement) => ({
            measurement,
            conflict,
          }))
        ),
        ({ measurement }) => chemistryMeasurementKey(measurement),
        (matches) => matches.map(({ conflict }) => conflict)
      )
    )
  );

export const conflictAppearance = (conflicts: ParameterConflict[]) =>
  conflicts.some(({ kind }) => kind !== "material_limit_excludes_material")
    ? { colour: "error.main", label: "impossibleCombination" as const }
    : { colour: "warning.main", label: "materialExcludedByLimit" as const };
